import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from "@/components/ui/pagination";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Trash, Pencil, Mail, Clock, User, Users, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { examAssignmentsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import AssignExamModal from "../../components/admin/AssignExamModal";
import EditAssignmentModal from "../../components/admin/EditAssignmentModal";
import { format } from "date-fns";

const ExamAssignment = () => {
  const PAGE_LIMIT = 5; // Single constant for items per page
  const [mySearchInput, setMySearchInput] = useState("");
  const [companySearchInput, setCompanySearchInput] = useState("");
  const [mySearchTerm, setMySearchTerm] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [myPage, setMyPage] = useState(1);
  const [companyPage, setCompanyPage] = useState(1);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const { toast } = useToast();

  // Sorting states for both tables
  const [mySortField, setMySortField] = useState('');
  const [mySortOrder, setMySortOrder] = useState('asc');
  const [companySortField, setCompanySortField] = useState('');
  const [companySortOrder, setCompanySortOrder] = useState('asc');

  // Debounce search for My Assignments
  useEffect(() => {
    const timer = setTimeout(() => {
      setMySearchTerm(mySearchInput);
      setMyPage(1);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [mySearchInput]);

  // Debounce search for Company Assignments
  useEffect(() => {
    const timer = setTimeout(() => {
      setCompanySearchTerm(companySearchInput);
      setCompanyPage(1);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [companySearchInput]);

  // Sorting functions
  const handleMySort = (field) => {
    if (mySortField === field) {
      setMySortOrder(mySortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setMySortField(field);
      setMySortOrder('asc');
    }
  };

  const handleCompanySort = (field) => {
    if (companySortField === field) {
      setCompanySortOrder(companySortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setCompanySortField(field);
      setCompanySortOrder('asc');
    }
  };

  const getSortIcon = (field, currentSortField, currentSortOrder) => {
    if (currentSortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return currentSortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortAssignments = (assignments, sortField, sortOrder) => {
    // If no sorting is applied, return assignments in their original order (newest first from API)
    // This ensures newly created assignments appear at the top across all pages
    if (!sortField) return assignments;

    return [...assignments].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle date fields
      if (sortField === 'dateOfExam') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Added review status badge function from Code 1
  const getReviewStatusBadge = (status) => {
    const configs = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Under Review" },
      reviewed: { color: "bg-green-100 text-green-800", text: "Reviewed" },
      flagged: { color: "bg-red-100 text-red-800", text: "Flagged" },
    };
    const config = configs[status] || configs.pending;

    return (
      <Badge variant="secondary" className={config.color}>
        {config.text}
      </Badge>
    );
  };

  const {
    data: myData,
    isLoading: myLoading,
    isError: myError,
    refetch: refetchMy,
  } = useQuery({
    queryKey: ['my-exam-assignments', myPage, PAGE_LIMIT, mySearchTerm],
    queryFn: () => examAssignmentsAPI.getMyAssignments({
      page: myPage,
      limit: PAGE_LIMIT,
      search: mySearchTerm
    }),
    keepPreviousData: true,
  });

  const {
    data: companyData,
    isLoading: companyLoading,
    isError: companyError,
    refetch: refetchCompany,
  } = useQuery({
    queryKey: ['company-exam-assignments', companyPage, PAGE_LIMIT, companySearchTerm],
    queryFn: () => examAssignmentsAPI.getCompanyAssignments({
      page: companyPage,
      limit: PAGE_LIMIT,
      search: companySearchTerm
    }),
    keepPreviousData: true,
  });



  const handleDeleteAssignment = async (id) => {
    if (!confirm("Are you sure you want to delete this exam assignment?")) return;

    try {
      await examAssignmentsAPI.delete(id);
      toast({
        title: "Assignment deleted",
        description: "The exam assignment has been deleted successfully",
      });
      refetchMy();
      refetchCompany();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete assignment",
        variant: "destructive",
      });
    }
  };

  const handleResendInvitation = async (id) => {
    try {
      await examAssignmentsAPI.resendInvitation(id);
      toast({
        title: "Invitation resent",
        description: "The invitation email has been resent successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to resend invitation",
        variant: "destructive",
      });
    }
  };

  const handleEditAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setIsEditModalOpen(true);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case 'ongoing':
        return "bg-blue-100 text-blue-800 border-blue-200";
      case 'completed':
        return "bg-green-100 text-green-800 border-green-200";
      case 'disqualified':
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const renderAssignmentTable = (assignments, title, icon, searchInput, setSearchInput, page, setPage, pagination, sortField, sortOrder, handleSort) => {
    // Sort the assignments
    const sortedAssignments = sortAssignments(assignments, sortField, sortOrder);

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          {icon}
          <h3 className="text-lg font-semibold">{title}</h3>
          <span className="text-sm text-gray-500">({pagination?.total || 0})</span>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              type="search"
              placeholder="Search by candidate name, email, or exam name..."
              className="pl-8 w-full"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>

        {assignments.length > 0 ? (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>S.No</TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('candidateId.name')}
                    >
                      <div className="flex items-center">
                        Candidate
                        {getSortIcon('candidateId.name', sortField, sortOrder)}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('candidateId.email')}
                    >
                      <div className="flex items-center">
                        Email
                        {getSortIcon('candidateId.email', sortField, sortOrder)}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('examId.name')}
                    >
                      <div className="flex items-center">
                        Exam
                        {getSortIcon('examId.name', sortField, sortOrder)}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('dateOfExam')}
                    >
                      <div className="flex items-center">
                        Scheduled For
                        {getSortIcon('dateOfExam', sortField, sortOrder)}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('status', sortField, sortOrder)}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('reviewStatus')}
                    >
                      <div className="flex items-center">
                        Review Status
                        {getSortIcon('reviewStatus', sortField, sortOrder)}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('assignedBy.name')}
                    >
                      <div className="flex items-center">
                        Assigned By
                        {getSortIcon('assignedBy.name', sortField, sortOrder)}
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAssignments.map((assignment, index) => (
                    <TableRow key={assignment._id}>
                      <TableCell className="font-medium">{(page - 1) * PAGE_LIMIT + index + 1}</TableCell>
                      <TableCell className="font-medium">
                        {assignment.candidateId?.name}
                      </TableCell>
                      <TableCell>{assignment.candidateId?.email}</TableCell>
                      <TableCell>{assignment.examId?.name}</TableCell>
                      <TableCell>
                        {assignment.dateOfExam ? (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-gray-500" />
                            {format(new Date(assignment.dateOfExam), "MMM dd, yyyy HH:mm")}
                          </div>
                        ) : 'Not scheduled'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(assignment.status)}>
                          {assignment.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {/* Added review status display from Code 1 */}
                        {assignment.reviewStatus ?
                          getReviewStatusBadge(assignment.reviewStatus) :
                          "Not Applicable"}
                      </TableCell>
                      <TableCell>{assignment.assignedBy?.name}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-1">
                          <Button variant="ghost" size="icon" onClick={() => handleResendInvitation(assignment._id)} title="Resend invitation email">
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Edit assignment" onClick={() => handleEditAssignment(assignment)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteAssignment(assignment._id)} title="Delete assignment">
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {pagination?.totalPages > 1 && (
              <Pagination className="mt-4">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      disabled={page === 1}
                      className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  {[...Array(pagination.totalPages).keys()].map((i) => {
                    const pageNumber = i + 1;
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          isActive={page === pageNumber}
                          onClick={() => setPage(pageNumber)}
                          className={`cursor-pointer ${
                            page === pageNumber
                              ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                              : ""
                          }`}
                        >
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                      disabled={page === pagination.totalPages}
                      className={page === pagination.totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        ) : (
          <div className="text-center p-6 text-gray-500 border rounded-md">
            No exam assignments found in this category
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Exam Assignment</h1>
        <Button onClick={() => setIsAssignModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Assign Exam
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Manage Exam Assignments</CardTitle>
        </CardHeader>
        <CardContent>
          {(myLoading || companyLoading) ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary border-t-primary"></div>
            </div>
          ) : (myError || companyError) ? (
            <div className="text-center p-6 text-red-500">
              Failed to load exam assignments. Please try again.
            </div>
          ) : (
            <Accordion type="multiple" defaultValue={["my-assignments", "other-assignments"]} className="w-full">
              <AccordionItem value="my-assignments">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    <span>My Assignments ({myData?.data?.pagination?.total || 0})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {renderAssignmentTable(
                    myData?.data?.assignments || [],
                    "Exam assignments created by you",
                    <User className="h-5 w-5 text-blue-600" />,
                    mySearchInput,
                    setMySearchInput,
                    myPage,
                    setMyPage,
                    myData?.data?.pagination,
                    mySortField,
                    mySortOrder,
                    handleMySort
                  )}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="other-assignments">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Company Assignments ({companyData?.data?.pagination?.total || 0})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {renderAssignmentTable(
                    companyData?.data?.assignments || [],
                    "Exam assignments created by other company users",
                    <Users className="h-5 w-5 text-green-600" />,
                    companySearchInput,
                    setCompanySearchInput,
                    companyPage,
                    setCompanyPage,
                    companyData?.data?.pagination,
                    companySortField,
                    companySortOrder,
                    handleCompanySort
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>

      <AssignExamModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={() => {
          // Reset to page 1 and clear sorting to show newest assignments first
          setMyPage(1);
          setCompanyPage(1);
          setMySortField('');
          setMySortOrder('asc');
          setCompanySortField('');
          setCompanySortOrder('asc');
          
          // Refetch both tables
          refetchMy();
          refetchCompany();
          
          // Close the modal
          setIsAssignModalOpen(false);
          
          // Show success message
          toast({
            title: "Assignment created",
            description: "The exam assignment has been created successfully and appears at the top of the list",
          });
        }}
      />

      <EditAssignmentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        assignmentId={selectedAssignment?._id}
        onSuccess={() => {
          // Refetch both tables to show updated data
          refetchMy();
          refetchCompany();
          
          // Close the modal
          setIsEditModalOpen(false);
          
          // Show success message
          toast({
            title: "Assignment updated",
            description: "The exam assignment has been updated successfully",
          });
        }}
      />
    </div>
  );
};

export default ExamAssignment;